import { RecommendationTrackingData } from "./RecommendationTrackingData";

declare const RECOMMENDATION_API_BASE_URL: string;

import { TrackingEventType } from './TrackingEventType';
import { ISessionService, SessionCreatedEvent, SessionServiceReadyEvent } from '@studyportals/student-interfaces';
import { TrackingEntityType } from './TrackingEntityType';
import { IEventAggregationService, ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { UrlHandler } from "../../JS/Search/UrlHandler";

/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-enum-comparison */
export class UserInteractionTracker implements ISubscriber<SessionServiceReadyEvent|SessionCreatedEvent>{
	private readonly sessionIdKey: string
	private readonly trackingEndpoint: string;
	private bestFitTriggered = false;
	private pageViewTracked = false;
	private entityPageViewEventListener?: () => Promise<void>;
	public eventAggregationService: IEventAggregationService;
	private sessionService?: ISessionService;

	constructor() {
		this.eventAggregationService = window.EventAggregationService;
		this.sessionIdKey = 'UserInteractionTracker/SessionId';
		this.trackingEndpoint = `${RECOMMENDATION_API_BASE_URL}/track`;
	}

	public async init(): Promise<void> {
		this.subscribeToSessionService();
		this.subscribeToEvents();
	}

	public subscribeToSessionService(): void {
		if (!this.eventAggregationService) {
			return;
		}

		this.eventAggregationService.subscribeTo(SessionCreatedEvent.EventType, this, true);
		this.eventAggregationService.subscribeTo(SessionServiceReadyEvent.EventType, this, true);
	}

	public notify(event: SessionServiceReadyEvent | SessionCreatedEvent): void | Promise<void> {
		if(event.eventType === SessionServiceReadyEvent.EventType){
			this.sessionService = (event as SessionServiceReadyEvent).sessionService;
		}

		if(!this.sessionService) return;
		this.attachEntityPageListeners();
	}

	public attachEntityPageListeners(): void {
		const entityType = this.getEntityType();

		if (entityType && !this.entityPageViewEventListener) {
			this.entityPageViewEventListener = async (): Promise<void> => await this.trackEntityView(entityType);
			document.addEventListener('scroll', this.entityPageViewEventListener as any, {once: true});
			document.addEventListener('mousemove', this.entityPageViewEventListener as any, {once: true});
		}
	}


	public async trackEntityView(entityType: TrackingEntityType): Promise<void> {
		const eventType = this.getViewEventTypeFromEntityType(entityType);
		if (!eventType || !this.entityPageViewEventListener || this.pageViewTracked) {
			return;
		}

		this.pageViewTracked = true;

		await this.trackEvent(eventType, entityType);
	}

	private getViewEventTypeFromEntityType(entityType: TrackingEntityType): TrackingEventType | null {
		switch (entityType) {
			case TrackingEntityType.STUDY:
				return TrackingEventType.STUDY_PAGE_VIEW;
			case TrackingEntityType.ORGANISATION:
				return TrackingEventType.ORGANISATION_PAGE_VIEW;
			default:
				return null;
		}
	}

	public async getIdentityId(): Promise<string|null> {
		if (!this.sessionService) {
			return null;
		}

		const session = await this.sessionService?.getSession();
		if (!session) {
			return null;
		}

		const user = session.getUser();

		return user.identityId;
	}

	public validateEventType(eventType: string): boolean {
		if (!eventType) {
			console.error('UserInteractionTracker: eventType is not provided');
			return false;
		}

		const supportedEventTypes = Object.values(TrackingEventType);
		const isValidEvent = supportedEventTypes.some((supportedEventType: TrackingEventType) => supportedEventType === eventType);

		if (!isValidEvent) {
			console.error('UserInteractionTracker: eventType is not valid');
			return false;
		}

		return true;
	}

	public async trackEvent(eventType: TrackingEventType, type: TrackingEntityType, id?: string): Promise<void> {
		const isValidEvent = this.validateEventType(eventType);
		if (!isValidEvent) {
			return;
		}

		const entityId = id ? id : this.getEntityId();
		if (!entityId) {
			return;
		}

		const payload: RecommendationTrackingData = {
			sessionId: this.getSessionId(),
			itemId: `${type}-${entityId}`,
			itemType: type,
			eventType
		};

		const identityId = await this.getIdentityId();
		if (identityId) {
			payload.userId = identityId;
		}

		try {
			await fetch(this.trackingEndpoint, {
				method: 'POST',
				body: JSON.stringify(payload)
			});
		} catch (e: any) {
			console.error('UserInteractionTracker: ' + e.toString());
		}
	}

	public getSessionId(): string {
		let id = sessionStorage.getItem(this.sessionIdKey);

		if (!id) {
			id = this.generateUniqueId();
			sessionStorage.setItem(this.sessionIdKey, id);
		}

		return id;
	}

	public getEntityId(): null | string {
		const trackingElement: HTMLElement | null = document.querySelector('.TrackingEvent');

		if (!trackingElement || !trackingElement.dataset || !trackingElement.dataset.id) {
			return null;
		}

		return trackingElement.dataset.id;
	}

	public generateUniqueId(): string {
		return '_' + btoa(`${Math.random().toString(36).substr(2)}${new Date().getTime()}`);
	}

	public subscribeToEvents(): void {
		this.subscribeToBestFitOpenEvent();
		this.subscribeToFavouriteAdded();
		this.subscribeToReferralClicked();
		this.subscribeToReviewsClicked();
		this.subscribeToOrganisationLinkClicked();
	}

	public subscribeToBestFitOpenEvent(): void {
		document.addEventListener('bestfit_overview_opened', (event: any) => {
			if (!event || !event.detail || !event.detail.studyId) {
				return;
			}
			if (this.bestFitTriggered) {
				return;
			}

			void this.trackEvent(TrackingEventType.BESTFIT_OPEN, TrackingEntityType.STUDY, event.detail.studyId as string);
		});

		document.addEventListener('profile_questionnaires_initialize', (event: any) => {
			if (!event || !event.detail || !event.detail.identifier) {
				return;
			}
			if (event.detail.identifier !== 'bestfit') {
				return;
			}
			if (!event.detail.isPopupQuestionnaire) {
				return;
			}

			const studyId = this.getEntityId();
			if (UrlHandler.isStudyPage() && studyId) {
				void this.trackEvent(TrackingEventType.BESTFIT_OPEN, TrackingEntityType.STUDY, studyId);
				this.bestFitTriggered = true;
			}
		});
	}

	public subscribeToFavouriteAdded(): void {
		document.addEventListener('favourite_added_interaction', (event: any) => {
			if (!event || !event.detail || !event.detail.studyId) {
				return;
			}

			void this.trackEvent(TrackingEventType.ADD_TO_WISHLIST, TrackingEntityType.STUDY, event.detail.studyId as string);
		});
	}

	public subscribeToReferralClicked(): void {
		const entityType = this.getEntityType();
		if (!entityType) {
			return;
		}

		document.addEventListener('referral_link_clicked', () => {
			void this.trackEvent(TrackingEventType.REFERRAL_CLICK, entityType);
		});

		this.bindReferralLinkClickEvents();
	}

	public subscribeToReviewsClicked(): void {
		const entityType = this.getEntityType();
		if (!entityType) {
			return;
		}

		document.addEventListener('reviews_opened', () => {
			void this.trackEvent(TrackingEventType.REVIEWS_OPEN, entityType);
		});
	}

	public subscribeToOrganisationLinkClicked(): void {
		document.addEventListener('organisation_link_clicked', () => {
			void this.trackEvent(TrackingEventType.ORGANISATION_PAGE_CLICK, TrackingEntityType.STUDY);
		});
	}

	public bindReferralLinkClickEvents(): void {
		if (UrlHandler.isStudyPage()) {
			const elements = document.getElementsByClassName('StudyLink TrackingExternalLink');
			Array.from(elements).forEach((element) => {
				element.addEventListener('click', () => {
					document.dispatchEvent(new Event('referral_link_clicked'));
				});
			});
		}

		if (UrlHandler.isOrganisationPage()) {
			const elements = document.getElementsByClassName('UniversityPageReferralButton TrackingExternalLink');
			Array.from(elements).forEach((element) => {
				element.addEventListener('click', () => {
					document.dispatchEvent(new Event('referral_link_clicked'));
				});
			});
		}
	}

	public getEntityType(): TrackingEntityType | null {
		if (UrlHandler.isStudyPage()) {
			return TrackingEntityType.STUDY;
		}

		if (UrlHandler.isOrganisationPage()) {
			return TrackingEntityType.ORGANISATION;
		}

		return null;
	}
}

const userInteractionTracker = new UserInteractionTracker();
void userInteractionTracker.init();
